/**
 * Every tunable number of the game, named after the GDD (docs/GDD.md). Values marked [T]
 * there are tuned with `pnpm sim`; any change goes in the GDD tuning log (§17).
 *
 * Conventions: time in milliseconds, alarm in tenths of a percent point (0–1000),
 * ratios in permille. Nothing here is a float.
 */

import type { Difficulty, GearId, PuzzleType } from './types.ts';

export interface DifficultyBalance {
  /** Nominal execution timer, GDD §9. */
  timerMs: number;
  /** Inclusive puzzle level range, GDD §9. */
  levelMin: 1 | 2 | 3 | 4;
  levelMax: 2 | 3 | 4 | 5;
  /** Multiplier applied to *error* alarm penalties only (GDD §6.2). */
  penaltyPermille: number;
}

export const DIFFICULTY: Record<Difficulty, DifficultyBalance> = {
  easy: { timerMs: 600_000, levelMin: 1, levelMax: 2, penaltyPermille: 750 },
  normal: { timerMs: 480_000, levelMin: 2, levelMax: 3, penaltyPermille: 1000 },
  pro: { timerMs: 420_000, levelMin: 3, levelMax: 4, penaltyPermille: 1250 },
  legend: { timerMs: 360_000, levelMin: 4, levelMax: 5, penaltyPermille: 1500 },
};

export const BALANCE = {
  // ---- Phases (GDD §2) -------------------------------------------------------------
  briefingMs: 45_000,
  planningMs: 180_000,
  planVoteMs: 30_000,
  discussionMs: 90_000,
  accusationMs: 30_000,
  splitProposeMs: 45_000,
  splitVoteMs: 20_000,
  revealMs: 60_000,

  // ---- Clock -----------------------------------------------------------------------
  /** The server never sends a tick longer than this (GDD §16, item 28). */
  maxTickMs: 1000,
  /** Long ticks are processed in steps of at most this, so timed events fire in order. */
  tickStepMs: 250,

  // ---- Players ---------------------------------------------------------------------
  minPlayers: 3,
  maxPlayers: 5,
  /** Probability that an infiltrator exists, per player count (GDD §4.1). */
  infiltratorChancePermille: { 3: 400, 4: 550, 5: 700 } as Record<number, number>,
  /** Timer compensation for the roles that are missing (GDD §9, [T]). */
  playerCountTimerPermille: { 3: 1100, 4: 1050, 5: 1000 } as Record<number, number>,
  /** Espresso delle Alpi is tighter (GDD §9). */
  expressTimerPermille: 800,

  // ---- Alarm (GDD §6) --------------------------------------------------------------
  alarmMax: 1000,
  policeAlertAt: 500,
  optionalCloseAt: 750,
  policeTimerRatePermille: 1500,
  rushTimerRatePermille: 1200,
  /** Error penalties in alarm tenths, before the difficulty multiplier (GDD §6.2). */
  penalty: {
    lock: 40,
    safe: 50,
    panel: 60,
    cameras: 60,
    laser: 80,
    password: 40,
    corridor: 150,
    vault: 100,
  } as Record<PuzzleType, number>,
  /** Fixed amounts (not scaled by difficulty). */
  fixed: {
    petard: 50,
    tipoff: 150,
    serviceEntrance: 100,
    cameraDrip: 20,
  },
  cameraDripEveryMs: 20_000,

  // ---- Planning (GDD §5.2) ---------------------------------------------------------
  gearBudget: 10,
  gearCost: { proPick: 3, jammer: 4, rope: 2, disguise: 3, drone: 3, emp: 5 } as Record<
    GearId,
    number
  >,
  maxDetours: 3,
  routeMinRooms: 4,
  routeMaxRooms: 6,
  maxRejections: 2,
  roofTimerCostMs: 60_000,
  ropeHopMs: 10_000,
  detourReturnMs: 10_000,
  droneMs: 30_000,

  // ---- Powers (GDD §3.2) -----------------------------------------------------------
  planBCharges: 2,
  planBWindowMs: 90_000,
  loopCharges: 3,
  loopMs: 20_000,
  coins: 2,
  petards: 1,
  coinDivertRounds: 2,
  coinRangeSteps: 3,
  petardDivertRounds: 3,

  // ---- Infiltrator and events (GDD §4) --------------------------------------------
  tipoffDelayMs: 20_000,
  rushMs: 30_000,
  minEventGapMs: 25_000,
  minRandomEventSpacingMs: 40_000,
  noSabotageFirstMs: 20_000,
  randomEventsMin: 2,
  randomEventsMax: 4,
  /** Chance that the driver's radio announces a random event beforehand. */
  bulletinChancePermille: 550,
  bulletinLeadMinMs: 15_000,
  bulletinLeadMaxMs: 25_000,
  /** Deferral when an event has nothing to act on yet. */
  eventRetryMs: 10_000,

  // ---- Puzzles ---------------------------------------------------------------------
  puzzlesPerRoomTwoPermille: 600,
  vaultPrePuzzlePermille: 500,
  safeLockMs: 30_000,
  safeRefillAttempts: 3,
  camerasMaxFailures: 3,
  camerasLockMs: 15_000,
  laserRechargeMs: 3_000,
  corridorIdleMs: 12_000,
  corridorMaxRounds: 40,
  vaultErrorsAllowed: 2,
  reinforcementEveryMs: 25_000,
  /** Police cars at the start of the escape = alarm / this (tenths). */
  policeCarEveryTenths: 125,
  maxPoliceCars: 8,
  /** Node cap for the escape solvability check; inconclusive means "unsafe". */
  escapeSolveNodeCap: 30_000,

  // ---- Tunnels on the night train (GDD §10.4) -------------------------------------
  tunnelEveryMs: 90_000,
  tunnelMs: 10_000,

  // ---- Scoring (GDD §7) ------------------------------------------------------------
  lootPenaltyStartTenths: 500,
  lootPenaltyStepTenths: 100,
  lootPenaltyPercentPerStep: 10,
  infiltratorScorePercent: 60,
  maxSplitRejections: 3,
} as const;

/** Training mode (GDD §11, [T]). */
export const PRACTICE = {
  /** Par time = reference solve time × (1 + this × (level − 1)). */
  parPerLevelPermille: 150,
  /** The clock runs out at this multiple of the par time. */
  limitFactor: 3,
  /** Stars: solved within `timeFactor` × par with at most `maxAlarmTenths` of alarm. */
  threeStars: { timeFactor: 1, maxAlarmTenths: 200 },
  twoStars: { timeFactor: 2, maxAlarmTenths: 500 },
} as const;

/** Reference solve times (GDD §8, [T]) used for plan estimates and bot pacing. */
export const REFERENCE_SOLVE_MS: Record<PuzzleType, number> = {
  lock: 35_000,
  safe: 45_000,
  panel: 30_000,
  cameras: 35_000,
  laser: 40_000,
  password: 40_000,
  corridor: 30_000,
  escape: 40_000,
  vault: 45_000,
};
